/**
 * Fortunio Flip - Microgame Page
 * 
 * A push-your-luck card game where players tap cards to reveal them.
 * Each card can increase your multiplier, but hit a bust and lose all!
 * 
 * Per Step 28 spec:
 * - Server-authoritative (all outcomes from API)
 * - Tap any card to reveal (no separate reveal button)
 * - Uses existing toast system for notifications
 * - Respects AnimationLevel preference
 * - data-testid attributes for E2E testing
 */

import { logger } from '../lib/logger';
import { getAnimationPref, type AnimationTier } from '../lib/animation-pref';
import { showError } from '../lib/toast';
import { showResumeBanner } from '../lib/resume-banner';
import { triggerCelebration } from '../stores/celebration-store';
import { safeInitCelebrations } from '../utils/celebration-handler';
import { runSequence, type SequenceHandle } from '../lib/animation-orchestrator';
import { fireAndForgetCelebration, delayEffect } from '../lib/animation-effects';
import { showEndOfGameOverlay, buildSoloWinConfig, buildSoloBustConfig, type EndOfGameConfig, type NextGameSuggestions } from '../components/end-of-game-overlay';
import { initAnimationsModal } from '../components/animation-tier-modal';
import { UserAPI } from '../utils/user-api';
import { balanceService } from '../services/balance-service';
import { authModal, ensureAuthModalExists } from '../components/auth-modal';
import { fireGuestEvent, fireGameCompleted, hookGuestCompletionToEOG } from '../lib/guest-conversion-engine';
import { isGuestUser } from '../lib/auth-state';

// Types
interface DifficultyConfig {
  id: string;
  game_key: string;
  label: string;
  base_fp: number;
  deck_size: number;
  bust_count: number;
  multiplier_curve: number[];
  max_safe_reveals: number;
  min_safe_before_collect: number;
  bust_penalty: number;
  status: string;
}

interface RoundState {
  id: string;
  game_key: string;
  difficulty_id: string;
  status: 'active' | 'busted' | 'collected' | 'abandoned';
  safe_reveals: number;
  multiplier: number;
  potential_fp: number;
  revealed_positions: Record<string, string>;  // {index: 'safe'|'bust'}
  created_at: string;
}

interface GameConfig {
  enabled: boolean;
  guest_play: boolean;
  daily_limit: number;
  active_skin_url: string | null;
  difficulties: DifficultyConfig[];
}

interface RevealResponse {
  result: 'safe' | 'bust';
  card_index: number;
  safe_reveals: number;
  multiplier: number;
  potential_fp: number;
  status: string;
  can_collect: boolean;
  revealed_positions: Record<string, string>;
  bust_penalty?: number;
  play_event_id?: string;
  next_game_suggestions?: NextGameSuggestions;
}

interface CollectResponse {
  status: string;
  awarded_fp: number;
  is_guest: boolean;
  rewards: Array<{ amount: number; reason: string }>;
  achievements: Array<{ id: string; name: string; points: number }>;
  total_fp: number;
  play_event_id?: string;
  next_game_suggestions?: NextGameSuggestions;
}

// State
let currentConfig: GameConfig | null = null;
let currentRound: RoundState | null = null;
let currentDifficulty: DifficultyConfig | null = null;
let isProcessing = false;
let isGuest = false;
let lastPlayEventId: string | undefined;
let lastNextGameSuggestions: NextGameSuggestions | undefined;
let pendingEntryContext: string | undefined;
let eogHandle: SequenceHandle | undefined;

// API Base URL
const API_BASE = '/api/games/fortunio-flip';

async function loadGuestStatus(): Promise<void> {
  try {
    const resp = await fetch('/me', { credentials: 'same-origin' });
    if (resp.ok) {
      const data = await resp.json();
      isGuest = isGuestUser(data);
    } else {
      isGuest = true;
    }
  } catch {
    isGuest = true;
  }
  logger.info(`[Flip] Guest status: ${isGuest}`);
}

// Animation preference
let animationLevel: AnimationTier = 'playful';

function loadAnimationLevel(): void {
  animationLevel = getAnimationPref();
}

/** Map animation level to CSS tier class name */
function getAnimTierClass(): string {
  const level = animationLevel;
  if (level === 'none') return 'flip-anim-none';
  if (level === 'reduced') return 'flip-anim-reduced';
  return 'flip-anim-playful';
}

// API Calls
async function fetchConfig(): Promise<GameConfig> {
  const response = await fetch(`${API_BASE}/config`);
  if (!response.ok) {
    throw new Error('Failed to load game configuration');
  }
  return response.json();
}

async function fetchState(): Promise<{ round: RoundState | null; config: DifficultyConfig | null; can_collect: boolean; is_guest: boolean }> {
  const response = await fetch(`${API_BASE}/state`);
  if (!response.ok) {
    throw new Error('Failed to load game state');
  }
  return response.json();
}

async function startRound(difficultyId: string): Promise<{ round: RoundState; config: DifficultyConfig; existing: boolean; is_guest: boolean }> {
  // Consume entry context once (from URL param on first start)
  const ec = pendingEntryContext;
  pendingEntryContext = undefined;

  const response = await fetch(`${API_BASE}/start`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ difficulty_id: difficultyId, ...(ec ? { entry_context: ec } : {}) }),
  });
  if (!response.ok) {
    const error = await response.json();
    // Attach status code for daily limit detection (F9)
    const err = new Error(error.error || 'Failed to start round') as Error & { status?: number; dailyLimit?: number };
    err.status = response.status;
    if (error.daily_limit) err.dailyLimit = error.daily_limit;
    throw err;
  }
  return response.json();
}

async function revealCard(roundId: string, cardIndex: number): Promise<RevealResponse> {
  const response = await fetch(`${API_BASE}/reveal`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ round_id: roundId, card_index: cardIndex }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to reveal card');
  }
  return response.json();
}

async function collectWinnings(roundId: string): Promise<CollectResponse> {
  const response = await fetch(`${API_BASE}/collect`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ round_id: roundId }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to collect');
  }
  return response.json();
}

// DOM Elements (lazy-loaded)
function getElement<T extends HTMLElement>(id: string): T | null {
  return document.getElementById(id) as T | null;
}

// UI Updates
function showScreen(screen: 'loading' | 'difficulty' | 'game' | 'result') {
  const loadingState = getElement('loadingState');
  const difficultySelection = getElement('difficultySelection');
  const gameArea = getElement('gameArea');
  const resultScreen = getElement('resultScreen');
  const gameControls = getElement('gameControls');
  
  if (loadingState) loadingState.style.display = screen === 'loading' ? 'block' : 'none';
  if (difficultySelection) difficultySelection.style.display = screen === 'difficulty' ? 'block' : 'none';
  if (gameArea) gameArea.style.display = screen === 'game' ? 'block' : 'none';
  if (resultScreen) resultScreen.style.display = screen === 'result' ? 'block' : 'none';
  // Show game controls (Animations button) on all screens except loading
  if (gameControls) gameControls.style.display = screen !== 'loading' ? 'block' : 'none';
}

/** Update stat counters + collect button (no DOM rebuild of cards). */
function updateStats() {
  if (!currentRound || !currentDifficulty) return;

  const multiplierDisplay = getElement('multiplierDisplay');
  const potentialFpDisplay = getElement('potentialFpDisplay');
  const safeRevealsDisplay = getElement('safeRevealsDisplay');
  const currentDifficultyLabel = getElement('currentDifficultyLabel');
  const collectBtn = getElement<HTMLButtonElement>('collectBtn');

  if (multiplierDisplay) {
    multiplierDisplay.textContent = `${currentRound.multiplier}×`;
  }
  if (potentialFpDisplay) {
    potentialFpDisplay.textContent = String(currentRound.potential_fp);
  }
  if (safeRevealsDisplay) {
    safeRevealsDisplay.textContent = String(currentRound.safe_reveals);
  }
  if (currentDifficultyLabel) {
    currentDifficultyLabel.textContent = currentDifficulty.label;
  }

  const canCollect = currentRound.safe_reveals >= currentDifficulty.min_safe_before_collect;
  if (collectBtn) {
    collectBtn.disabled = !canCollect;
  }
}

/** Full update: stats + full deck rebuild (for initial render / resume). */
function updateGameDisplay() {
  updateStats();
  updateDeckVisual();
}

function updateDeckVisual() {
  const deckVisual = getElement('deckVisual');
  if (!deckVisual || !currentDifficulty || !currentRound) return;

  const deckSize = currentDifficulty.deck_size;
  const revealedPositions = currentRound.revealed_positions || {};
  const isActive = currentRound.status === 'active';
  const tierClass = getAnimTierClass();
  
  let html = `<div class="${tierClass}"><div class="flip-cards">`;
  
  for (let i = 0; i < deckSize; i++) {
    const posKey = String(i);
    const revealed = revealedPositions[posKey];
    
    if (revealed) {
      // Already-revealed card — render in final state, skip animation.
      // Use "is-pre-revealed" so CSS can suppress animations/transitions.
      const backClass = revealed === 'safe' ? 'is-safe' : 'is-bust';
      const emoji = revealed === 'safe' ? '✨' : '💣';
      html += `<div class="flip-anim-card is-revealed is-pre-revealed" data-card-index="${i}">
        <div class="flip-anim-card-inner">
          <div class="flip-anim-front"><i class="bi bi-question"></i></div>
          <div class="flip-anim-back ${backClass}"><span class="flip-card-emoji">${emoji}</span></div>
        </div>
      </div>`;
    } else {
      // Unrevealed card — front/back structure, clickable if active
      const clickable = isActive ? 'flip-card-clickable' : '';
      const skinStyle = currentConfig?.active_skin_url
        ? ` style="background-image: url('${currentConfig.active_skin_url}'); background-size: cover; background-position: center;"`
        : '';
      html += `<div class="flip-anim-card ${clickable}" data-card-index="${i}" data-testid="card-${i}">
        <div class="flip-anim-card-inner">
          <div class="flip-anim-front"${skinStyle}>${currentConfig?.active_skin_url ? '' : '<i class="bi bi-question"></i>'}</div>
          <div class="flip-anim-back"></div>
        </div>
      </div>`;
    }
  }
  
  html += '</div></div>';
  deckVisual.innerHTML = html;
  
  // Add click handlers to unrevealed cards
  if (isActive) {
    deckVisual.querySelectorAll('.flip-anim-card:not(.is-revealed)').forEach(card => {
      card.addEventListener('click', handleCardClick);
    });
  }
}

/**
 * Reveal a single card in-place — modifies the existing DOM element so that
 * CSS transitions (reduced) and animations (playful) fire correctly.
 * Only the clicked card animates; the rest of the deck is untouched.
 */
function revealSingleCard(cardEl: HTMLElement, outcome: 'safe' | 'bust'): void {
  // Remove interactive state
  cardEl.classList.remove('flip-card-clickable', 'flip-anim-processing');
  cardEl.removeAttribute('data-testid');

  // Populate the back face with the correct outcome
  const backFace = cardEl.querySelector('.flip-anim-back');
  if (backFace) {
    backFace.classList.add(outcome === 'safe' ? 'is-safe' : 'is-bust');
    backFace.innerHTML = `<span class="flip-card-emoji">${outcome === 'safe' ? '✨' : '💣'}</span>`;
  }

  // Trigger the reveal — this flips the card via CSS transition (reduced)
  // or fires the keyframe animation (playful).
  cardEl.classList.add('is-revealed');
  cardEl.classList.add(outcome === 'safe' ? 'is-safe-reveal' : 'is-bust-reveal');
}

function setStatusMessage(message: string, type: 'info' | 'success' | 'warning' | 'danger' = 'info', action?: { label: string; handler: () => void }) {
  const statusMessage = getElement('statusMessage');
  if (!statusMessage) return;
  
  if (action) {
    statusMessage.innerHTML = '';
    const btn = document.createElement('button');
    btn.className = 'btn btn-primary btn-sm';
    btn.textContent = action.label;
    btn.addEventListener('click', action.handler);
    statusMessage.appendChild(btn);
  } else {
    const iconMap = {
      info: 'bi-info-circle',
      success: 'bi-check-circle',
      warning: 'bi-exclamation-triangle',
      danger: 'bi-x-circle',
    };
    
    statusMessage.innerHTML = `
      <span class="text-${type}">
        <i class="bi ${iconMap[type]} me-1"></i>${message}
      </span>
    `;
  }
}

function renderDifficultyButtons() {
  const difficultySelector = document.querySelector('[data-testid="difficulty-selector"]');
  if (!difficultySelector || !currentConfig) return;

  const buttons = currentConfig.difficulties.map(diff => {
    const riskLabel = diff.id === 'chill' ? 'Low Risk' : diff.id === 'risky' ? 'High Risk' : 'Balanced';
    const bustInfo = diff.bust_penalty > 0 ? `<small class="d-block text-danger mt-1">Bust: -${diff.bust_penalty} FP</small>` : '';
    return `
      <div class="card gc-card gc-card-vertical" data-difficulty="${diff.id}" data-testid="difficulty-${diff.id}">
        <div class="card-body d-flex justify-content-between align-items-center">
          <div>
            <div class="gc-card-label">${diff.label}</div>
            <div class="gc-card-tagline">${riskLabel}</div>
            ${bustInfo}
          </div>
          <div class="text-end">
            <small class="text-muted">Collect multiples of:</small> <span class="fp-badge">✨ ${diff.base_fp} FP</span>
          </div>
        </div>
      </div>
    `;
  }).join('');

  difficultySelector.innerHTML = buttons;

  // Add click handlers
  difficultySelector.querySelectorAll('.gc-card').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      const diffId = (e.currentTarget as HTMLElement).dataset.difficulty;
      if (diffId) {
        await handleStartRound(diffId);
      }
    });
  });
}

function showDailyLimitInfo() {
  const dailyLimitInfo = getElement('dailyLimitInfo');
  const dailyLimitText = getElement('dailyLimitText');
  
  if (!currentConfig || !dailyLimitInfo || !dailyLimitText) return;
  
  if (currentConfig.daily_limit > 0) {
    dailyLimitInfo.style.display = 'block';
    dailyLimitText.textContent = `Daily limit: ${currentConfig.daily_limit} rounds`;
  } else {
    dailyLimitInfo.style.display = 'none';
  }
}

// Event Handlers
async function handleStartRound(difficultyId: string) {
  if (isProcessing) return;
  isProcessing = true;

  try {
    logger.log('Starting round with difficulty:', difficultyId);
    
    const response = await startRound(difficultyId);
    currentRound = response.round;
    currentDifficulty = response.config;

    if (response.existing) {
      logger.log('Resuming existing active round');
    }

    updateGameDisplay();
    showScreen('game');
    window.__versionGuard?.setSafeToReload(false);
    
    // Save difficulty preference (authenticated users only)
    if (!response.is_guest) {
      UserAPI.setPreference('flip_difficulty', difficultyId).catch(() => {});
    }

    logger.log('Round started:', currentRound.id);
  } catch (err) {
    logger.error('Failed to start round:', err);
    // Check for daily limit (F9)
    const typedErr = err as Error & { status?: number };
    if (typedErr.status === 429) {
      const modalEl = document.getElementById('dailyLimitModal');
      if (modalEl) {
        const modal = new bootstrap.Modal(modalEl);
        modal.show();
      }
    } else {
      showError(err instanceof Error ? err.message : 'Failed to start round');
    }
  } finally {
    isProcessing = false;
  }
}

async function handleCardClick(event: Event) {
  if (isProcessing || !currentRound || currentRound.status !== 'active') return;
  
  const card = event.currentTarget as HTMLElement;
  const cardIndex = parseInt(card.dataset.cardIndex || '-1', 10);
  
  if (cardIndex < 0) return;
  
  // Client-side check: prevent clicking already-revealed cards
  const revealedPositions = currentRound.revealed_positions || {};
  if (revealedPositions[String(cardIndex)]) {
    logger.log('Card already revealed, ignoring click:', cardIndex);
    return;
  }
  
  isProcessing = true;
  
  // Immediately disable this card to prevent double-click
  card.classList.remove('flip-card-clickable');
  card.classList.add('flip-anim-processing');

  try {
    const response = await revealCard(currentRound.id, cardIndex);
    
    // Update state from server
    currentRound.safe_reveals = response.safe_reveals;
    currentRound.multiplier = response.multiplier;
    currentRound.potential_fp = response.potential_fp;
    currentRound.status = response.status as RoundState['status'];
    currentRound.revealed_positions = response.revealed_positions;
    lastPlayEventId = response.play_event_id;
    if (response.next_game_suggestions) {
      lastNextGameSuggestions = response.next_game_suggestions;
    }

    // Reveal this single card in-place (animate only this card)
    revealSingleCard(card, response.result);

    // Update stats (multiplier, FP, collect button) without rebuilding deck
    updateStats();

    const animLevel = animationLevel;
    
    if (response.result === 'safe') {
      // Celebration for high multiplier
      if (animLevel !== 'none' && response.multiplier >= 5) {
        setStatusMessage(`${response.multiplier}× multiplier! Keep going?`, 'success', { label: `${response.multiplier}× multiplier! Keep going?`, handler: handlePlayAgain });
        triggerCelebration('big_win');
      } else if (response.safe_reveals === 1) {
        setStatusMessage('Good start! Keep going?', 'info', { label: 'Good start! Keep going?', handler: handlePlayAgain });
      } else {
        setStatusMessage('Safe! Risk another?', 'info', { label: 'Safe! Risk another?', handler: handlePlayAgain });
      }
    } else {
      // Bust — card already revealed in-place above
      
      if (animLevel !== 'none') {
        setStatusMessage('Bust! Game over.', 'danger');
      }
      
      // FIX-03: Refresh navbar FP balance after bust penalty deduction
      if (response.bust_penalty && response.bust_penalty > 0) {
        balanceService.refresh().catch(err => logger.error('FP refresh failed:', err));
      }
      
      // Show result screen after a delay
      setTimeout(() => {
        window.__versionGuard?.setSafeToReload(true);
        showResultScreen('bust', undefined, response.bust_penalty, lastPlayEventId, lastNextGameSuggestions);
      }, 1000);
    }

  } catch (err) {
    logger.error('Failed to reveal card:', err);
    showError(err instanceof Error ? err.message : 'Failed to reveal card');
    // Re-enable the card on error
    card.classList.add('flip-card-clickable');
    card.classList.remove('flip-anim-processing');
  } finally {
    isProcessing = false;
  }
}

async function handleCollect() {
  if (isProcessing || !currentRound) return;
  isProcessing = true;

  const collectBtn = getElement<HTMLButtonElement>('collectBtn');
  if (collectBtn) collectBtn.disabled = true;

  try {
    const response = await collectWinnings(currentRound.id);
    
    currentRound.status = 'collected';
    
    // FIX-04: Refresh navbar FP balance after collecting winnings
    if (response.total_fp > 0 && !response.is_guest) {
      balanceService.refresh().catch(err => logger.error('FP refresh failed:', err));
    }
    
    window.__versionGuard?.setSafeToReload(true);
    showResultScreen('collect', response, undefined, response.play_event_id ?? lastPlayEventId, response.next_game_suggestions ?? lastNextGameSuggestions);
    
    // FIX-08: Removed FP award toast — end-of-game overlay already shows FP earned.
    // Rule: no toast when a modal is shown for the same event.
    // Celebration is now triggered by the orchestrator in showResultScreen().

    logger.log('Collected:', response);
  } catch (err) {
    logger.error('Failed to collect:', err);
    showError(err instanceof Error ? err.message : 'Failed to collect');
    if (collectBtn) collectBtn.disabled = false;
  } finally {
    isProcessing = false;
  }
}

function showResultScreen(type: 'bust' | 'collect', response?: CollectResponse, bustPenalty?: number, playEventId?: string, nextGameSuggestions?: NextGameSuggestions) {
  const achievements = response?.achievements?.map(a => ({ name: a.name, points: a.points }));

  let config: EndOfGameConfig;

  if (type === 'bust') {
    // Bust — use shared bust builder
    config = buildSoloBustConfig({
      game: 'flip',
      fpPenalty: bustPenalty,
      isGuest,
      onPlayAgain: () => { void fireGameCompleted(isGuest); handlePlayAgain(); },
      playAgainUrl: '/games/fortunio-flip',
      achievements,
      playEventId,
      nextGameSuggestions,
    });
  } else {
    // Collect / Win — use shared solo-win builder then override Flip-specific copy
    const fpAmount = response?.total_fp ?? 0;
    config = buildSoloWinConfig({
      game: 'flip',
      totalScore: fpAmount,
      fpEarned: fpAmount > 0 ? fpAmount : undefined,
      isGuest,
      onPlayAgain: () => { void fireGameCompleted(isGuest); handlePlayAgain(); },
      playAgainUrl: '/games/fortunio-flip',
      playEventId,
      nextGameSuggestions,
    });
    // Override with Flip-specific messaging
    config.message = 'Nice timing! 🎉';
    config.subtitle = 'You banked your winnings just in time.';
    if (!isGuest && !nextGameSuggestions) {
      config.primaryAction.label = 'Play Again & Keep Your Streak?';
    }
    // Flip collect shows guest CTA only on collect, not bust
    if (isGuest) {
      config.rewards = { ...config.rewards, isGuest: true };
    }
    if (achievements) {
    }
  }

  // Cancel any pending EOG sequence
  eogHandle?.cancel();

  config.playEventId = playEventId;

  // Gold shower for collect wins — particles run ~2.7s but modal opens
  // after 1s so the shower keeps falling behind the overlay.
  const isCollectWin = type === 'collect' && response?.total_fp && response.total_fp > 0 && !response.is_guest;

  // Orchestrated: fire-and-forget celebration → 1s delay → overlay
  eogHandle = runSequence('flip-eog', [
    ...(isCollectWin ? [fireAndForgetCelebration('flip_collect')] : []),
    delayEffect(isCollectWin ? 1000 : 0),
  ], () => {
    showEndOfGameOverlay(config);
    hookGuestCompletionToEOG(isGuest);
    void loadStats();
  });
}

function handlePlayAgain() {
  eogHandle?.cancel();
  eogHandle = undefined;
  window.__versionGuard?.setSafeToReload(true);
  currentRound = null;
  showScreen('difficulty');
}

async function loadStats(): Promise<void> {
  const statsCard = document.getElementById('statsCard');
  if (!statsCard) return;

  if (isGuest) {
    const cardBody = statsCard.querySelector('.card-body');
    if (cardBody) {
      cardBody.innerHTML = `
        <h6 class="text-muted mb-3">Your Stats</h6>
        <div class="text-center py-3" data-testid="guest-stats-placeholder">
          <p class="mb-1 text-muted">Your game stats will appear here</p>
          <p class="small text-muted mb-2">Available when signed in</p>
          <a href="#" class="btn btn-outline-primary btn-sm" data-testid="guest-stats-signin">
            Sign in to start tracking →
          </a>
        </div>`;
      const signinLink = cardBody.querySelector('[data-testid="guest-stats-signin"]');
      if (signinLink) {
        signinLink.addEventListener('click', (e) => {
          e.preventDefault();
          void fireGuestEvent('tried_locked_feature', true).then((shown) => {
            if (!shown) {
              ensureAuthModalExists();
              authModal.show();
            }
          });
        });
      }
    }
    return;
  }

  try {
    const resp = await fetch('/api/games/fortunio-flip/stats');
    if (!resp.ok) return;
    const data = await resp.json() as { total_rounds: number; collect_rate: number; best_payout: number };
    const rounds = document.getElementById('statRounds');
    const collectRate = document.getElementById('statCollectRate');
    const bestPayout = document.getElementById('statBestPayout');
    if (rounds) rounds.textContent = String(data.total_rounds);
    if (collectRate) collectRate.textContent = `${data.collect_rate}%`;
    if (bestPayout) bestPayout.textContent = `${data.best_payout} FP`;
  } catch (err) {
    logger.warn('[Flip] Failed to load stats', err);
  }
}

// =============================================================================
// Animations Modal (shared component)
// =============================================================================

function initFlipAnimationsModal(): void {
  initAnimationsModal({
    gamePrefix: 'flip',
    onTierChange: (tier) => {
      animationLevel = tier as AnimationTier;

      // Re-render deck to apply new tier class (if a round is active)
      if (currentRound && currentRound.status === 'active') {
        updateDeckVisual();
      }
    },
  });
}

// Initialization

  // Read URL params for guided play flow
  const urlParams = new URLSearchParams(window.location.search);
  const difficultyParam = urlParams.get('difficulty');
  const ecParam = urlParams.get('ec');
  if (ecParam) {
    pendingEntryContext = ecParam;
  }
  // Clean guided-play params from URL
  if (ecParam || difficultyParam) {
    const cleanUrl = new URL(window.location.href);
    cleanUrl.searchParams.delete('ec');
    cleanUrl.searchParams.delete('difficulty');
    history.replaceState(null, '', cleanUrl.toString());
  }
async function init() {
  logger.log('Initializing Fortunio Flip...');
  
  showScreen('loading');

  // Load animation preference before rendering anything
  loadAnimationLevel();

  try {
    // Load game config
    currentConfig = await fetchConfig();
    logger.log('Config loaded:', currentConfig);

    // Preload active skin image; clear URL on load error so we fall back to CSS gradient (F15)
    if (currentConfig.active_skin_url) {
      const img = new Image();
      img.onerror = () => {
        logger.log('Skin image failed to load, falling back to CSS gradient');
        if (currentConfig) currentConfig.active_skin_url = null;
      };
      img.src = currentConfig.active_skin_url;
    }

    // Render difficulty buttons
    renderDifficultyButtons();
    showDailyLimitInfo();

    // Load guest status early (like Hangman/Letter Shuffle)
    loadGuestStatus().then(() => loadStats());

    // Check for existing active round
    const stateResponse = await fetchState();
    if (stateResponse.round && stateResponse.round.status === 'active') {
      currentRound = stateResponse.round;
      currentDifficulty = stateResponse.config;
      updateGameDisplay();
      showScreen('game');
      window.__versionGuard?.setSafeToReload(false);

      // Show resume banner above the game card
      const gameArea = getElement('gameArea');
      if (gameArea) {
        showResumeBanner({
          container: gameArea,
          onAbandon: async () => {
            try {
              await fetch(`${API_BASE}/abandon`, { method: 'POST' });
            } catch (err) {
              logger.error('Failed to abandon round:', err);
            }
            currentRound = null;
            currentDifficulty = null;
            showScreen('difficulty');
          },
        });
      }
    } else if (difficultyParam && difficultyParam !== 'auto') {
      // Auto-start with specific difficulty from URL param (guided play)
      await handleStartRound(difficultyParam);
    } else if (difficultyParam === 'auto') {
      // 'auto' → try saved preference, fall back to difficulty selection screen
      const saved = await UserAPI.getPreference('flip_difficulty').catch(() => null);
      if (typeof saved === 'string' && saved) {
        await handleStartRound(saved);
      } else {
        showScreen('difficulty');
      }
    } else {
      showScreen('difficulty');
    }

    // Set up event listeners
    const collectBtn = getElement<HTMLButtonElement>('collectBtn');
    const playAgainBtn = getElement<HTMLButtonElement>('playAgainBtn');
    
    if (collectBtn) {
      collectBtn.addEventListener('click', handleCollect);
    }
    if (playAgainBtn) {
      playAgainBtn.addEventListener('click', handlePlayAgain);
    }

    // Initialize Bootstrap tooltips
    const tooltipTriggerList = Array.from(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
    tooltipTriggerList.forEach(el => new bootstrap.Tooltip(el as HTMLElement));

    // Initialize animations modal
    initFlipAnimationsModal();

    // Initialize celebration particle system
    safeInitCelebrations();

    // Keyboard shortcuts
    document.addEventListener('keydown', (e) => {
      if (currentRound?.status === 'active' && !isProcessing) {
        if (e.key === 'c' || e.key === 'C') {
          e.preventDefault();
          if (currentDifficulty && currentRound.safe_reveals >= currentDifficulty.min_safe_before_collect) {
            handleCollect();
          }
        }
      }
    });

    logger.log('Fortunio Flip initialized');
  } catch (err) {
    logger.error('Failed to initialize:', err);
    showError('Failed to load game. Please refresh the page.');
    showScreen('difficulty');
  }
}

// Start initialization - handle both DOMContentLoaded and already-loaded states
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  // DOM already loaded, init immediately
  init();
}
